"use client";

import React, { type ReactNode, useCallback, useMemo, useState } from "react";

import type { MapPoints } from "@shared/types/map-types";
import { parseYandexCoords } from "@shared/utils";

import { MAMISON_RESORT } from "../constants/map.constants";
import { YMapsProvider } from "../providers/yandex-maps-context";
import { getBounds } from "../utils/bounds-calculator";
import { LocationMap } from "./location-map/location-map";

export interface LocationMapWithProviderProps {
  coordinates?: string;
  zoom?: number;
  className?: string;
  mapPoints?: MapPoints;
  openPointId?: string | null;
  primaryObjectId?: string | null;
  alwaysOpenResort?: boolean;
  enableScrollZoom?: boolean;
  children?: ReactNode;
  /** Если true, центр и зум будут рассчитаны автоматически по всем точкам на карте */
  useAutoBounds?: boolean;
  /** Отступ для расчета границ (0.1 = 10%) */
  boundsPadding?: number;
  /** Если true, использует initialBounds вместо initialCenter/initialZoom */
  useBounds?: boolean;

  /**
   * Ручной фокус карты (например, клик по станции/объекту). При наличии
   * имеет приоритет над bounds/autoBounds.
   */
  focusLocation?: {
    center: [number, number];
    zoom?: number;
  } | null;

  /**
   * Ограничение для расчета bounds: точки дальше этого расстояния от курорта не учитываются.
   * Используется для resort map, где cultural-objects могут быть далеко за пределами зоны курорта.
   */
  boundsMaxDistanceKm?: number;

  /**
   * Левый отступ (в px) для расчета bounds, чтобы учесть перекрывающую карту левую панель.
   */
  leftPaddingPx?: number;
}

/**
 * LocationMapWithProvider - компонент, который инкапсулирует YMapsProvider
 * и LocationMap, позволяя передавать начальные параметры центра и зума карты.
 * Поддерживает автоматический расчет центра и зума по границам всех точек.
 * Реализация полностью соответствует официальной документации Яндекс Maps API 3.0.
 */
export const LocationMapWithProvider: React.FC<
  LocationMapWithProviderProps
> = ({
  coordinates,
  zoom = 18,
  className,
  mapPoints,
  openPointId = null,
  primaryObjectId = null,
  alwaysOpenResort = false,
  enableScrollZoom = false,
  children,
  useAutoBounds = false,
  boundsPadding = 0.1,
  useBounds = false,
  focusLocation = null,
  boundsMaxDistanceKm,
  leftPaddingPx = 0,
}) => {
  const haversineKm = (a: [number, number], b: [number, number]): number => {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(b[1] - a[1]);
    const dLng = toRad(b[0] - a[0]);
    const lat1 = toRad(a[1]);
    const lat2 = toRad(b[1]);
    const s1 = Math.sin(dLat / 2);
    const s2 = Math.sin(dLng / 2);
    const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  };

  const [markersLayout, setMarkersLayout] = useState<{
    containerWidth: number;
    maxMarkerWidth: number;
  } | null>(null);

  const onMarkersLayout = useCallback(
    (layout: { containerWidth: number; maxMarkerWidth: number }) => {
      setMarkersLayout(layout);
    },
    [],
  );

  const normalizedCoordinates = useMemo(() => {
    if (!coordinates) return null;
    try {
      const parsed = parseYandexCoords(coordinates);
      if (parsed && Array.isArray(parsed) && parsed.length === 2) {
        return parsed as [number, number];
      }
    } catch (error) {
      console.warn("Failed to parse coordinates:", coordinates, error);
    }
    return null;
  }, [coordinates]);

  const normalizedMapPoints = useMemo(() => {
    if (!mapPoints) return undefined;

    const normalizePoint = <T extends { coordinates?: any }>(point: T): T => {
      if (!point?.coordinates) return point;
      if (Array.isArray(point.coordinates) && point.coordinates.length === 2) {
        return point;
      }
      try {
        const parsed = parseYandexCoords(String(point.coordinates));
        return { ...point, coordinates: parsed };
      } catch (error) {
        console.warn("[LocationMapWithProvider] failed to parse point coords", {
          coordinates: point.coordinates,
          error,
        });
        return point;
      }
    };

    const normalizeArray = <T extends { coordinates?: any }>(arr?: T[]) =>
      Array.isArray(arr) ? arr.map(normalizePoint) : arr;

    return {
      ...mapPoints,
      webcams: normalizeArray(mapPoints.webcams),
      "cafes-restaurants": normalizeArray(mapPoints["cafes-restaurants"]),
      accommodations: normalizeArray(mapPoints.accommodations),
      stations: normalizeArray(mapPoints.stations),
      "cultural-objects": normalizeArray(mapPoints["cultural-objects"]),
      resort: mapPoints.resort
        ? normalizePoint(mapPoints.resort)
        : mapPoints.resort,
    } as MapPoints;
  }, [mapPoints]);

  const normalizedLngLatList = useMemo(() => {
    if (!normalizedMapPoints) return [] as [number, number][];

    const resortCenter = (() => {
      const rc = (normalizedMapPoints.resort as any)?.coordinates;
      if (Array.isArray(rc) && rc.length === 2) return rc as [number, number];
      try {
        return parseYandexCoords(MAMISON_RESORT.coordinates) as [
          number,
          number,
        ];
      } catch {
        return null;
      }
    })();

    const list: [number, number][] = [];
    const push = (v: any) => {
      if (Array.isArray(v) && v.length === 2) {
        const lng = Number(v[0]);
        const lat = Number(v[1]);
        if (Number.isFinite(lng) && Number.isFinite(lat)) list.push([lng, lat]);
      }
    };

    const pushWithFilter = (v: any) => {
      if (typeof boundsMaxDistanceKm === "number" && resortCenter) {
        if (Array.isArray(v) && v.length === 2) {
          const pt: [number, number] = [Number(v[0]), Number(v[1])];
          if (!Number.isFinite(pt[0]) || !Number.isFinite(pt[1])) return;
          const d = haversineKm(resortCenter, pt);
          if (d > boundsMaxDistanceKm) return;
        }
      }

      push(v);
    };

    const arrays = [
      { key: "webcams", points: normalizedMapPoints.webcams },
      {
        key: "cafes-restaurants",
        points: normalizedMapPoints["cafes-restaurants"],
      },
      { key: "accommodations", points: normalizedMapPoints.accommodations },
      {
        key: "cultural-objects",
        points: normalizedMapPoints["cultural-objects"],
      },
      { key: "stations", points: normalizedMapPoints.stations },
    ];

    arrays.forEach((points) => {
      if (Array.isArray(points.points)) {
        points.points.forEach((p) => {
          pushWithFilter((p as any).coordinates);
        });
      }
    });

    if (normalizedMapPoints.resort) {
      push((normalizedMapPoints.resort as any).coordinates);
    }

    return list;
  }, [normalizedMapPoints, boundsMaxDistanceKm]);

  const baseBounds = useMemo(() => {
    if (!normalizedLngLatList.length) return null;
    return getBounds(normalizedLngLatList);
  }, [normalizedLngLatList]);

  const adjustedBounds = useMemo(() => {
    if (!baseBounds) return null;
    if (!markersLayout || markersLayout.containerWidth <= 0) return baseBounds;

    const [min, max] = baseBounds;
    const lngSpan = max[0] - min[0];
    if (!Number.isFinite(lngSpan) || lngSpan <= 0) return baseBounds;

    const extraRightPx = markersLayout.maxMarkerWidth;
    const extraRightRatio = extraRightPx / markersLayout.containerWidth;
    const extraRightLng = lngSpan * extraRightRatio;

    const extraLeftPx = leftPaddingPx;
    const extraLeftRatio = extraLeftPx / markersLayout.containerWidth;
    const extraLeftLng = lngSpan * extraLeftRatio;

    return [
      [min[0] - extraLeftLng, min[1]],
      [max[0] + extraRightLng, max[1]],
    ] as [[number, number], [number, number]];
  }, [baseBounds, markersLayout, leftPaddingPx]);

  const paddedBounds = useMemo(() => {
    if (!adjustedBounds) return null;
    const [[minLng, minLat], [maxLng, maxLat]] = adjustedBounds;
    const lngSpan = maxLng - minLng;
    const latSpan = maxLat - minLat;

    if (!Number.isFinite(lngSpan) || !Number.isFinite(latSpan))
      return adjustedBounds;

    const lngPad = lngSpan * boundsPadding;
    const latPad = latSpan * boundsPadding;

    return [
      [minLng - lngPad, minLat - latPad],
      [maxLng + lngPad, maxLat + latPad],
    ] as [[number, number], [number, number]];
  }, [adjustedBounds, boundsPadding]);

  // Расчет начальных параметров карты (только из нормализованных данных)
  const initialParams = useMemo(() => {
    if (focusLocation?.center) {
      const focusZoom =
        typeof focusLocation.zoom === "number" ? focusLocation.zoom : zoom;

      return {
        initialCenter: focusLocation.center,
        initialZoom: focusZoom,
        initialBounds: undefined,
      };
    }

    if ((useBounds || useAutoBounds) && paddedBounds) {
      return {
        initialBounds: paddedBounds,
      };
    }

    if (normalizedCoordinates) {
      return {
        initialCenter: normalizedCoordinates,
        initialZoom: zoom,
      };
    }

    return {
      initialCenter: [43.839767, 42.662089] as [number, number],
      initialZoom: zoom,
    };
  }, [
    focusLocation,
    useBounds,
    useAutoBounds,
    paddedBounds,
    markersLayout,
    normalizedCoordinates,
    coordinates,
    zoom,
  ]);

  return (
    <YMapsProvider
      initialCenter={initialParams.initialCenter}
      initialZoom={initialParams.initialZoom}
      initialBounds={initialParams.initialBounds}
    >
      <LocationMap
        coordinates={normalizedCoordinates}
        zoom={initialParams.initialZoom}
        className={className}
        mapPoints={normalizedMapPoints}
        openPointId={openPointId}
        primaryObjectId={primaryObjectId}
        alwaysOpenResort={alwaysOpenResort}
        enableScrollZoom={enableScrollZoom}
        bounds={initialParams.initialBounds}
        onMarkersLayout={onMarkersLayout}
      />
      {children}
    </YMapsProvider>
  );
};
